import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MetricsRawDump } from './MetricsRawDump';
import { ApiKeyMetricRow, MetricsSummary, UrlMetricRow } from './MetricsSummary';
import {
  METRIC_NAMES,
  metricValuesByLabel,
  parsePrometheus,
} from '../utils/prometheusParser';
import {
  avgDeltaPerCount,
  formatLocalTime,
  formatMsFromSeconds,
  ratePerSecond,
  TimestampedValue,
} from '../utils/metricsRates';
import { fetchMetricsText } from '../auth/metricsFetch';

const POLL_INTERVAL_MS = 10_000;
const MAX_SAMPLES = 8;
const ACTIVE_EPSILON = 0.01;

interface MetricsTabProps {
  active: boolean;
}

interface ApiKeyHistoryPoint {
  t: number;
  processed: number;
  pending: number;
}

interface UrlHistoryPoint {
  t: number;
  count: number;
  sum: number;
  max: number;
}

export function MetricsTab({ active }: MetricsTabProps) {
  const [apiKeyRows, setApiKeyRows] = useState<ApiKeyMetricRow[]>([]);
  const [urlRows, setUrlRows] = useState<UrlMetricRow[]>([]);
  const [lastFetch, setLastFetch] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiKeyHistoryRef = useRef<Map<string, ApiKeyHistoryPoint[]>>(new Map());
  const urlHistoryRef = useRef<Map<string, UrlHistoryPoint[]>>(new Map());

  const processSamples = useCallback((text: string) => {
    const now = Date.now();
    const samples = parsePrometheus(text);

    const processed = metricValuesByLabel(
      samples,
      METRIC_NAMES.messagesProcessed,
      'apiKey'
    );
    const pending = metricValuesByLabel(samples, METRIC_NAMES.pendingMessages, 'apiKey');
    const urlCounts = metricValuesByLabel(samples, METRIC_NAMES.urlProcessingCount, 'url');
    const urlSums = metricValuesByLabel(samples, METRIC_NAMES.urlProcessingSum, 'url');
    const urlMaxes = metricValuesByLabel(samples, METRIC_NAMES.urlProcessingMax, 'url');
    const urlStatus = metricValuesByLabel(samples, METRIC_NAMES.urlBackendStatus, 'url');

    const apiKeys = Array.from(
      new Set<string>([
        ...Array.from(processed.keys()),
        ...Array.from(pending.keys()),
      ])
    );
    const nextApiRows: ApiKeyMetricRow[] = [];

    apiKeys.forEach((apiKey) => {
      const processedVal = processed.get(apiKey) ?? 0;
      const pendingVal = pending.get(apiKey) ?? 0;

      const history = pushApiKeyHistory(apiKeyHistoryRef.current, apiKey, {
        t: now,
        processed: processedVal,
        pending: pendingVal,
      });

      const processedHistory: TimestampedValue[] = history.map((p) => ({
        t: p.t,
        v: p.processed,
      }));
      const processingTps = ratePerSecond(processedHistory);

      if (pendingVal <= 0 && (processingTps === null || processingTps < ACTIVE_EPSILON)) {
        return;
      }

      nextApiRows.push({
        apiKey,
        pending: pendingVal,
        processingTps,
      });
    });

    nextApiRows.sort((a, b) => b.pending - a.pending);

    const urls = Array.from(
      new Set<string>([
        ...Array.from(urlCounts.keys()),
        ...Array.from(urlSums.keys()),
        ...Array.from(urlMaxes.keys()),
        ...Array.from(urlStatus.keys()),
      ])
    );
    const nextUrlRows: UrlMetricRow[] = [];

    urls.forEach((url) => {
      const countVal = urlCounts.get(url) ?? 0;
      const sumVal = urlSums.get(url) ?? 0;
      const maxVal = urlMaxes.get(url);
      const statusVal = urlStatus.get(url);

      const history = pushUrlHistory(urlHistoryRef.current, url, {
        t: now,
        count: countVal,
        sum: sumVal,
        max: maxVal ?? 0,
      });

      const countHistory: TimestampedValue[] = history.map((p) => ({ t: p.t, v: p.count }));
      const sumHistory: TimestampedValue[] = history.map((p) => ({ t: p.t, v: p.sum }));
      const postRatePerSec = ratePerSecond(countHistory);
      const avgSeconds = avgDeltaPerCount(countHistory, sumHistory);

      if (postRatePerSec === null || postRatePerSec < ACTIVE_EPSILON) {
        return;
      }

      let status: UrlMetricRow['status'] = '—';
      if (statusVal !== undefined) {
        status = statusVal >= 1 ? 'UP' : 'DOWN';
      }

      nextUrlRows.push({
        url,
        status,
        postRatePerSec,
        avgLatencyMs: formatMsFromSeconds(avgSeconds),
        maxLatencyMs: maxVal !== undefined ? formatMsFromSeconds(maxVal) : '—',
      });
    });

    nextUrlRows.sort((a, b) => (b.postRatePerSec ?? 0) - (a.postRatePerSec ?? 0));

    setApiKeyRows(nextApiRows);
    setUrlRows(nextUrlRows);
  }, []);

  const refreshSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const text = await fetchMetricsText();
      processSamples(text);
      setLastFetch(formatLocalTime(new Date()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch /metrics');
    } finally {
      setLoading(false);
    }
  }, [processSamples]);

  useEffect(() => {
    if (!active) {
      return;
    }
    refreshSummary();
    const id = window.setInterval(refreshSummary, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [active, refreshSummary]);

  return (
    <div className="metrics-tab">
      <MetricsSummary
        apiKeyRows={apiKeyRows}
        urlRows={urlRows}
        lastFetch={lastFetch}
        loading={loading}
        error={error}
      />
      <hr className="metrics-tab-divider" />
      <MetricsRawDump />
    </div>
  );
}

function pushApiKeyHistory(
  map: Map<string, ApiKeyHistoryPoint[]>,
  apiKey: string,
  point: ApiKeyHistoryPoint
): ApiKeyHistoryPoint[] {
  const prev = map.get(apiKey) ?? [];
  const next = [...prev, point];
  const trimmed = next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next;
  map.set(apiKey, trimmed);
  return trimmed;
}

function pushUrlHistory(
  map: Map<string, UrlHistoryPoint[]>,
  url: string,
  point: UrlHistoryPoint
): UrlHistoryPoint[] {
  const prev = map.get(url) ?? [];
  const next = [...prev, point];
  const trimmed = next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next;
  map.set(url, trimmed);
  return trimmed;
}
